import { DashboardSeverityKpi } from '@api';
import { CircleLoader } from '@components/ui';
import { MapperHelper } from '@helpers';
import { notionStore } from '@stores/notion.store';
import { settingsStore } from '@stores/settings.store';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartOptions, ChartData } from 'chart.js';
import { observer } from 'mobx-react';
import React, { useMemo, useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';

import { ChartCard } from '../ChartCard/ChartCard';

import styles from './RiskExposure.module.scss';

interface RiskExposureProbProps {
  kpis: DashboardSeverityKpi;
}

const labelFn = context => {
  const index = context.dataIndex;
  const { dataset } = context;
  const value = dataset.data[index];
  const startValue = dataset.data.slice(0, index).reduce((a, b) => a + b, 0);
  const endValue = startValue + value;
  const { label } = context;
  return `${label}: ${startValue}-${endValue}%`;
};

const labels = ['Minimal Risk', 'Limited Risk', 'High Risk', 'Unacceptable Risk'];

const data: ChartData<'doughnut'> & { datasets: any } = {
  labels,
  datasets: [
    {
      data: [25, 25, 25, 25],
      backgroundColor: ['#5d9f46e6', '#e3ba16', '#e67d22', '#c84c3c'],
      hoverBackgroundColor: ['#518d3be6', '#d1a245', '#d97620', '#b13f30'],
      borderColor: ['#518d3be6', '#c29439', '#ce6d18', '#b13f30'],
      borderWidth: 1,
      circumference: 180,
      rotation: 270,
      cutout: '55%',
      needleValue: 0,
    },
  ],
};

export const gaugeNeedle = {
  id: 'gaugeNeedle',
  afterDatasetDraw(chart: any) {
    const { ctx, data: chartData } = chart;

    ctx.save();
    const { x, y, outerRadius, innerRadius } = chart.getDatasetMeta(0).data[0] || {};
    const widthSlice = (outerRadius - innerRadius) / 2;

    const radius = 5;
    const dataTotal = chartData.datasets[0].data.reduce((a: number, b: number) => a + b, 0);
    const angle = Math.PI / 180;

    let { needleValue } = chartData.datasets[0];
    if (needleValue > dataTotal) needleValue = dataTotal;
    if (needleValue < 0) needleValue = dataTotal;
    if (Number.isNaN(needleValue)) needleValue = dataTotal;
    const circumference =
      chart.getDatasetMeta(0).data[0] &&
      (chart.getDatasetMeta(0).data[0].circumference / Math.PI / chartData.datasets[0].data[0]) * needleValue;

    ctx.translate(x, y);
    ctx.rotate(Math.PI * (circumference + 1.5));

    // needle
    ctx.beginPath(); // new shape
    ctx.strokeStyle = '#3d5a80';
    ctx.fillStyle = '#3d5a80';
    ctx.lineWidth = 1;
    ctx.moveTo(0 - radius, 0);
    ctx.lineTo(0, 0 - innerRadius - widthSlice);
    ctx.lineTo(0 + radius, 0);
    ctx.closePath();
    ctx.stroke();
    ctx.fill();

    // dot
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, angle * 360, false);
    ctx.fill();

    ctx.restore();
  },
};

const gaugeFlowMeter = {
  id: 'gaugeFlowMeter',
  afterDatasetDraw(chart: any) {
    const { ctx, data: chartData } = chart;
    const ranges = [
      { min: 1, max: chart.data.datasets[0].data[0], label: labels[0] },
      {
        min: chart.data.datasets[0].data[0],
        max: chart.data.datasets[0].data[0] + chart.data.datasets[0].data[1],
        label: labels[1],
      },
      {
        min: chart.data.datasets[0].data[0] + chart.data.datasets[0].data[1],
        max: chart.data.datasets[0].data[0] + chart.data.datasets[0].data[1] + chart.data.datasets[0].data[2],
        label: labels[2],
      },
      {
        min: chart.data.datasets[0].data[0] + chart.data.datasets[0].data[1] + chart.data.datasets[0].data[2],
        max: chart.data.datasets[0].data[3],
        label: labels[3],
      },
    ];

    const getRangeLabel = value => {
      let result = '';
      ranges.forEach(range => {
        if (value >= range.min && value <= range.max) {
          result = range.label;
        }
      });
      return result;
    };
    ctx.save();
    const { needleValue } = chartData.datasets[0];
    const { x, y } = chart.getDatasetMeta(0).data[0] || {};
    const circumference =
      chart.getDatasetMeta(0).data[0] &&
      (chart.getDatasetMeta(0).data[0].circumference / Math.PI / chartData.datasets[0].data[0]) * needleValue;
    const percentageValue = circumference * 100;

    ctx.font = 'bold 14px sans-serif';
    ctx.fillStyle = '#3d5a80';
    ctx.textAlign = 'center';
    ctx.fillText(`${Number.isNaN(percentageValue) ? 'Very high' : `${getRangeLabel(needleValue)}`}`, x + 1, y + 30);
  },
};

const gaugeLabels = {
  id: 'gaugeFlowMeter',
  afterDatasetDraw(chart: any) {
    const {
      ctx,
      chartArea: { left, right },
    } = chart;

    const { x, y, outerRadius, innerRadius } = chart.getDatasetMeta(0).data[0] || {};
    const widthSlice = (outerRadius - innerRadius) / 2;

    ctx.translate(x, y);
    ctx.font = 'bold 12px sans-serif';
    ctx.fillStyle = '#3d5a80';
    ctx.textAlign = 'center';
    ctx.fillText(`Low risk`, 0 - innerRadius - widthSlice, 0 + 14);
    ctx.fillText(`High risk`, 0 + innerRadius + widthSlice, 0 + 14);

    ctx.restore();
  },
};

ChartJS.register(ArcElement, Tooltip, Legend);

const options: ChartOptions<'doughnut'> = {
  plugins: {
    datalabels: { display: false },
    title: {
      display: false,
    },
    legend: {
      display: false,
    },
    tooltip: {
      padding: 8,
      backgroundColor: '#3d5a80d4',
      displayColors: false,
      callbacks: {
        label: labelFn,
        title: () => '',
      },
    },
  },
  aspectRatio: 1.4,
  responsive: true,
  layout: {
    padding: {
      bottom: 36,
      left: 10,
      right: 20,
    },
  },
  devicePixelRatio: 2.5,
};

export const RiskExposureProb: React.FC<RiskExposureProbProps> = observer(({ kpis }) => {
  const chartRef = useRef(null);
  const { values } = notionStore;

  const { riskLimits } = settingsStore;
  const limits: number[] | null = riskLimits?.overall?.split('|').map(number => parseInt(number, 10)) || null;

  const needleValue = useMemo(() => {
    if (!kpis) return 0;
    if (kpis.q95 * 100 < 0 || kpis.q95 * 100 > 100) return 100;
    return kpis.q95 * 100;
  }, [kpis]);

  const dataLimits = useMemo(() => {
    if (!limits) return null;

    return [limits[0], limits[1] - limits[0], limits[2] - limits[1], 100 - limits[2]];
  }, [limits]);

  return (
    <ChartCard
      title="Risk Exposure"
      subtitle="Estimated annual risk exposure of all use-cases in the filtered portfolio"
      icon="triangle-exclamation"
      iconClassName={styles.icon}
      className={styles.card}
      infoPosition="bottom"
      info={values?.riskExposure || ' '}
      chartStyle={{ marginBottom: '-50px' }}
    >
      <div className={styles.content}>
        <div className={styles.values}>
          <div>
            <div className={styles.label}>Average</div>
            {kpis !== undefined ? (
              <div
                className={styles.number}
                style={{ color: MapperHelper.getRiskIndicatorColor(kpis.mean * 100, limits) }}
              >
                {kpis.mean !== null ? `${MapperHelper.roundNumber(kpis.mean * 100, 2)}%` : '/'}
              </div>
            ) : (
              <CircleLoader
                small
                classNameLoader={styles.loader}
                className={styles.loaderWrapper}
              />
            )}
          </div>
          <div>
            <div className={styles.label}>Median</div>
            {kpis !== undefined ? (
              <div
                className={styles.number}
                style={{ color: MapperHelper.getRiskIndicatorColor(kpis.median * 100, limits) }}
              >
                {kpis.median !== null ? `${MapperHelper.roundNumber(kpis.median * 100, 2)}%` : '/'}
              </div>
            ) : (
              <CircleLoader
                small
                classNameLoader={styles.loader}
                className={styles.loaderWrapper}
              />
            )}
          </div>
          <div>
            <div className={styles.label}>75th Percentile</div>
            {kpis !== undefined ? (
              <div
                className={styles.number}
                style={{ color: MapperHelper.getRiskIndicatorColor(kpis.q75 * 100, limits) }}
              >
                {kpis.q75 !== null ? `${MapperHelper.roundNumber(kpis.q75 * 100, 2)}%` : '/'}
              </div>
            ) : (
              <CircleLoader
                small
                classNameLoader={styles.loader}
                className={styles.loaderWrapper}
              />
            )}
          </div>
          <div>
            <div className={styles.label}>Maximum</div>
            {kpis !== undefined ? (
              <div
                className={styles.number}
                style={{ color: MapperHelper.getRiskIndicatorColor(kpis.q100 * 100, limits) }}
              >
                {kpis.q100 !== null ? `${MapperHelper.roundNumber(kpis.q100 * 100, 2)}%` : '/'}
              </div>
            ) : (
              <CircleLoader
                small
                classNameLoader={styles.loader}
                className={styles.loaderWrapper}
              />
            )}
          </div>
        </div>
        <div className={styles.scale}>
          <Doughnut
            ref={chartRef}
            data={{
              ...data,
              datasets: [
                {
                  ...data.datasets[0],
                  needleValue,
                  data: dataLimits,
                },
              ],
            }}
            options={options}
            plugins={[gaugeNeedle, gaugeFlowMeter, gaugeLabels]}
          />
        </div>
      </div>
    </ChartCard>
  );
});
